// Swagger docs - https://dev-api.kuladao.io/
import { kulaApiFetch } from './kulaApiFetch.js'
import { toast } from '../../utilities/toast.js'
import { go } from '../../../shared/router/router-mixin.js'
import routes from '../../../shared/router/routes.js'

export const web3ApiUrl = process.env.KULA_API_URL

/**
 * Public API fetch function that doesn't include authentication credentials
 * @param {string} url
 * @param {{
 *  method?: 'GET'|'POST'|'PUT'|'DELETE'
 *  body?: any
 *  headers?: HeadersInit
 * }} settings
 */
function publicApiFetch(url, { method = 'GET', body, headers = {} }) {
  /** @type {RequestInit} */
  let reqInit = {
    method: method,
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...headers,
    },
    // Note: No credentials: 'include' here - this is for public endpoints
  }

  if (body) reqInit.body = JSON.stringify(body)

  return fetch(web3ApiUrl + url, reqInit)
}

/**
 * @param {{ email: string, password: string }} creds
 * @returns {Promise<SessionUser>}
 */
export function login(creds) {
  return kulaApiFetch('/v1/authentication/token', {
    method: 'POST',
    body: creds,
  })
    .then((response) => {
      return response.text().then((text) => {
        const errorResponse = JSON.parse(text)
        if (response.status === 200) {
          return JSON.parse(text)
        }
        if (
          errorResponse.description ===
          'totp code must be provided to verify device'
        ) {
          toast(errorResponse.description)
          window.dispatchEvent(new CustomEvent('new-device-detected'))
        }

        if (
          errorResponse.message ===
          'New device detected. A verification code has been sent to your email.'
        ) {
          toast(errorResponse.message)
          window.dispatchEvent(new CustomEvent('new-device-detected'))
        }

        if (response.status === 401) {
          toast('Invalid email or password.')
          window.dispatchEvent(new CustomEvent('login-auth-error'))
          throw new Error('An error occurred. Please try again.')
        } else {
          window.dispatchEvent(new CustomEvent('login-error'))
          toast('An error occurred. Please try again.')
          throw new Error('An error occurred. Please try again.')
        }
      })
    })
    .catch((error) => {
      console.error('Login error:', error)
      throw error
    })
}

export function refresh() {
  try {
    return kulaApiFetch('/v1/authentication/refresh', {
      method: 'POST',
    })
  } catch (error) {
    console.error('Refresh error:', error)
    throw error
  }
}

export function logout() {
  return kulaApiFetch('/v1/authentication/logout', {
    method: 'POST',
  }).then((response) => {
    if (response.status === 200) {
      go(routes.LOGIN.path)
    }
  })
}

export async function checkValidToken() {
  const response = await kulaApiFetch('/v1/authentication/valid', {
    method: 'POST',
  })

  if (response.status === 200) {
    return true
  }

  return false
}

/**
 * @param {UserSignup} user
 * @returns {Promise<User>}
 */
export function signup(user) {
  // enforce lower case in case backend is stupid and misses it
  user.email = user.email.toLowerCase()
  return kulaApiFetch('/v1/users/signup', {
    method: 'POST',
    body: user,
  }).then(async (response) => {
    const text = await response.text()
    if (!response.ok) {
      const errorData = JSON.parse(text)
      if (
        errorData.description?.includes('user for this email address') &&
        errorData.description?.includes('already exists')
      ) {
        toast('An account with this email already exists')
        throw new Error('User already exists')
      }
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      )
    }
    return JSON.parse(text)
  })
}

/**
 * @param {User} user - Updated User Data
 * @returns {Promise<User>}
 */
export function updateUser(user) {
  return kulaApiFetch('/v1/users/update', {
    method: 'PUT',
    body: user,
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  })
}

/**
 * Request password reset - sends reset email to user
 * @param {string} email - User's email address
 * @returns {Promise<void>}
 */
export function requestPasswordReset(email) {
  return publicApiFetch('/v1/users/password-reset', {
    method: 'POST',
    body: { email: email.toLowerCase() },
  })
    .then(async (response) => {
      if (response.status === 202) {
        toast('Password reset email sent. Please check your inbox.')
        return
      }

      const text = await response.text()
      const errorData = JSON.parse(text)

      if (response.status === 401) {
        toast('No account found with that email address.')
        throw new Error('Invalid email address')
      }

      toast('An error occurred. Please try again.')
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      )
    })
    .catch((error) => {
      console.error('Password reset request error:', error)
      throw error
    })
}

/**
 * Reset password using token from email
 * @param {string} email - User's email address
 * @param {string} newPassword - New password
 * @param {string} emailToken - Token from reset email
 * @returns {Promise<void>}
 */
export function resetPassword(email, newPassword, emailToken) {
  return publicApiFetch('/v1/users/new-password', {
    method: 'PUT',
    body: {
      email: email.toLowerCase(),
      password: newPassword,
      emailToken: emailToken,
    },
  })
    .then(async (response) => {
      if (response.status === 200) {
        toast(
          'Password reset successful. You can now log in with your new password.'
        )
        return
      }

      const text = await response.text()
      const errorData = JSON.parse(text)

      if (response.status === 401) {
        toast(
          'Invalid or expired reset token. Please request a new password reset.'
        )
        throw new Error('Invalid reset token')
      }

      toast('An error occurred. Please try again.')
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      )
    })
    .catch((error) => {
      console.error('Password reset error:', error)
      throw error
    })
}
